import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  AdminButtonComponent,
  AdminCardComponent,
  AdminConfirmDialogService,
  AdminPageHeaderComponent,
  AdminToastService,
} from '../../../shared/admin';
import { AuthService } from '../../../services/auth.service';
import { PermissionService } from '../../permissions/permission.service';
import {
  FEATURE_REQUEST_STATUSES,
  FEATURE_REQUEST_STATUS_LABELS,
  FEATURE_REQUEST_TYPE_LABELS,
  type FeatureRequestDetail,
  type FeatureRequestStatus,
} from '../../../shared/models/feature-request.models';
import { FeatureRequestsService } from '../../../shared/services/feature-requests.service';
import {
  PlatformUsersService,
  type AssignablePlatformUserDto,
} from '../../../shared/services/platform-users.service';
import { FeatureRequestActivityTimelineComponent } from './components/feature-request-activity-timeline.component';
import { FeatureRequestAttachmentsCardComponent } from './components/feature-request-attachments-card.component';
import { FeatureRequestCommentsCardComponent } from './components/feature-request-comments-card.component';
import { FeatureRequestPriorityBadgeComponent } from './components/feature-request-priority-badge.component';
import { FeatureRequestStatusBadgeComponent } from './components/feature-request-status-badge.component';
import { confirmFeatureRequestStatusChange } from './components/feature-request-status-change.util';

@Component({
  selector: 'app-feature-request-detail-page',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    RouterLink,
    AdminPageHeaderComponent,
    AdminCardComponent,
    AdminButtonComponent,
    FeatureRequestStatusBadgeComponent,
    FeatureRequestPriorityBadgeComponent,
    FeatureRequestCommentsCardComponent,
    FeatureRequestAttachmentsCardComponent,
    FeatureRequestActivityTimelineComponent,
  ],
  templateUrl: './feature-request-detail-page.component.html',
  styleUrl: './feature-request-detail-page.component.scss',
})
export class FeatureRequestDetailPageComponent implements OnInit {
  private readonly service = inject(FeatureRequestsService);
  private readonly usersService = inject(PlatformUsersService);
  private readonly auth = inject(AuthService);
  private readonly permissions = inject(PermissionService);
  private readonly toast = inject(AdminToastService);
  private readonly confirmDialog = inject(AdminConfirmDialogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private readonly activityRef = viewChild(FeatureRequestActivityTimelineComponent);
  private readonly commentsRef = viewChild(FeatureRequestCommentsCardComponent);

  readonly showStatusChange = computed(() =>
    this.permissions.canShow('featureRequests', 'statusChange'),
  );
  readonly canStatusChange = computed(() =>
    this.permissions.canExecute('featureRequests', 'statusChange'),
  );
  readonly showAssign = computed(() => this.permissions.canShow('featureRequests', 'assign'));
  readonly canAssign = computed(() => this.permissions.canExecute('featureRequests', 'assign'));
  readonly showDelete = computed(() => this.permissions.canShow('featureRequests', 'delete'));
  readonly canDelete = computed(() => this.permissions.canExecute('featureRequests', 'delete'));
  readonly showAdminControls = computed(
    () => this.showStatusChange() || this.showAssign() || this.showDelete(),
  );
  readonly isAdmin = computed(
    () => this.canStatusChange() || this.canAssign() || this.canDelete(),
  );
  readonly currentUserId = computed(() => this.auth.getCurrentUser()?.userId ?? '');

  readonly request = signal<FeatureRequestDetail | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly assignableUsers = signal<AssignablePlatformUserDto[]>([]);
  readonly statusSaving = signal(false);
  readonly assignSaving = signal(false);

  readonly statuses = FEATURE_REQUEST_STATUSES;
  readonly statusLabels = FEATURE_REQUEST_STATUS_LABELS;
  readonly typeLabels = FEATURE_REQUEST_TYPE_LABELS;

  assignTo = '';

  ngOnInit(): void {
    this.usersService.listAssignable().subscribe({
      next: (users) => this.assignableUsers.set(users),
    });
    this.load();
  }

  load(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) return;
    this.loading.set(true);
    this.service.getById(id).subscribe({
      next: (data) => {
        this.request.set(data);
        this.assignTo = data.assignedTo ?? '';
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Could not load feature request');
        this.loading.set(false);
      },
    });
  }

  async onStatusChange(req: FeatureRequestDetail, newStatus: FeatureRequestStatus): Promise<void> {
    const previous = req.status;
    if (newStatus === previous) return;

    const confirmed = await confirmFeatureRequestStatusChange(
      this.confirmDialog,
      newStatus,
      req.requestNumber,
    );
    if (!confirmed) {
      this.request.set({ ...req, status: previous });
      return;
    }

    this.request.set({ ...req, status: newStatus });
    this.statusSaving.set(true);
    this.service.updateStatus(req._id, newStatus).subscribe({
      next: (updated) => {
        this.request.set(updated);
        this.toast.success('Status updated');
        this.activityRef()?.reload();
        this.statusSaving.set(false);
      },
      error: () => {
        this.request.set({ ...req, status: previous });
        this.toast.error('Could not update status');
        this.statusSaving.set(false);
      },
    });
  }

  onAssignChange(): void {
    const req = this.request();
    if (!req) return;
    const value = this.assignTo || null;
    if ((req.assignedTo ?? '') === (value ?? '')) return;

    this.assignSaving.set(true);
    this.service.assign(req._id, value).subscribe({
      next: (updated) => {
        this.request.set(updated);
        this.assignTo = updated.assignedTo ?? '';
        this.toast.success('Assignment updated');
        this.activityRef()?.reload();
        this.assignSaving.set(false);
      },
      error: () => {
        this.assignTo = req.assignedTo ?? '';
        this.toast.error('Could not update assignment');
        this.assignSaving.set(false);
      },
    });
  }

  async deleteRequest(): Promise<void> {
    const req = this.request();
    if (!req) return;
    const confirmed = await this.confirmDialog.confirm({
      title: 'Delete feature request?',
      message: `Permanently delete ${req.requestNumber}? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    this.service.remove(req._id).subscribe({
      next: () => {
        this.toast.success('Feature request deleted');
        void this.router.navigate(['/platform/feature-requests']);
      },
      error: () => this.toast.error('Could not delete feature request'),
    });
  }

  onCommentsChanged(): void {
    this.activityRef()?.reload();
  }
}
